import React from 'react';
import { Button, MultiSelectDropdown, Popover, TextArea } from 'freemium-ui';
import { useToasts } from 'freemium-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { t } from 'i18next';
import { get } from 'lodash';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { useGetAllUsers } from 'routes/screens/settings/screens/user/hooks';
import Icon from 'shared/components/Icon';
import Spacer from 'shared/components/Spacer';
// import { useGetAccountUsers } from 'shared/hooks';
import { AccountUserType, SurveyResponseType, UserType } from 'shared/types';
import { useShareResponse } from './hooks';
import { StyledSharePopover } from './styles';

// import Icon from 'components/icons/AutoViewbox';
// import Spacer from 'components/Spacer';

const SHARE_RESPONSE_EMAILS_LIMIT = 10;

const getShareResponseSchema = (): Yup.AnyObjectSchema =>
  Yup.object().shape({
    emails: Yup.array()
      .min(1, `${t('forms.err_msg.min_email_req')}`)
      .of(Yup.string().email().required())
      .max(
        SHARE_RESPONSE_EMAILS_LIMIT,
        `${t('forms.err_msg.allowed_limits', {
          limit: `${SHARE_RESPONSE_EMAILS_LIMIT}`,
        })}`,
      ),
    notes: Yup.string()
      .nullable()
      .max(
        1500,
        `${t('forms.err_msg.note_max_char', {
          len: '1500',
        })}`,
      ),
  });

export type ShareResponseType = {
  emails?: string[];
  notes?: string;
};

type ShareResponseProps = {
  surveyResponse?: SurveyResponseType;
};

export const ShareResponse = (props: ShareResponseProps) => {
  const { t } = useTranslation();
  const { surveyResponse } = props;
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [, setToast] = useToasts();

  const shareResponseForm = useForm<ShareResponseType>({
    defaultValues: {
      emails: [],
      notes: '',
    },
    resolver: yupResolver(getShareResponseSchema()),
  });

  const {
    handleSubmit,
    register,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = shareResponseForm;

  const { data: accountUsersResponse } = useGetAllUsers({});
  const { mutate: shareResponse, isLoading } = useShareResponse({
    setToast,
    callback: (isSuccess: boolean) => {
      if (isSuccess) {
        setAnchorEl(null);
        reset({ emails: [], notes: '' });
      }
    },
  });

  const onSubmit = (data: ShareResponseType) => {
    shareResponse({
      payload: data,
      responseId: surveyResponse?.surveyserv_response_id || '',
      surveyId: surveyResponse?.survey_id || '',
    });
  };

  const setFormValue = (fieldId: any, fieldValue: any) => {
    setValue(fieldId, fieldValue, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const allEmails = React.useMemo<Record<string, UserType>>(() => {
    const vals: Record<string, UserType> = {};
    accountUsersResponse?.account_users?.forEach((user: UserType) => {
      if (user?.email) vals[user?.email] = user;
    });
    return vals;
  }, [accountUsersResponse]);

  const emails = watch('emails');

  const selectedItem =
    emails?.map((email: string) => allEmails[email] || { email }) || null;

  const allUsers = accountUsersResponse?.account_users.map(
    (item: AccountUserType) => item.user,
  );
  return (
    <>
      <Button
        id="response-share-btn"
        inline
        type="secondary"
        aria-describedby="share-popover"
        onClick={(e: any) => setAnchorEl(e.currentTarget)}
      >
        <Icon glyph="share" width="12px" height="12px" /> <Spacer x={0.3} />
        {t('share')}
      </Button>
      <Popover
        id="share-popover"
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <StyledSharePopover
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          isError={
            get(errors, ['emails'], []).length > 0 ||
            get(errors, ['emails', 'message'], false)
          }
        >
          <div className="share-header">
            <span className="email-dropdown-label">{t('to')}:&nbsp;</span>
            <div className="share-multiselect">
              <MultiSelectDropdown
                name="emails"
                placeholder={
                  emails && emails?.length > 0
                    ? ''
                    : `${t('forms.placeholders.enter_recipient_email')}`
                }
                items={allUsers || []}
                selectedItem={selectedItem}
                onChange={(selectedEmail: UserType[]) => {
                  setFormValue(
                    'emails',
                    selectedEmail.map((user) => user?.email || ''),
                  );
                }}
                customValues
                isErrored={
                  get(errors, ['emails'], []).length > 0 ||
                  get(errors, ['emails', 'message'], false)
                }
                errorText={get(
                  errors,
                  ['emails', 'message'],
                  `${t('forms.err_msg.email_validation')}`,
                )}
                itemLabelIdentifier="email"
                itemValueIdentifier="email"
              />
            </div>
          </div>
          <div className="share-content">
            <TextArea
              {...register('notes')}
              placeholder={`${t('forms.placeholders.optional_note')}`}
              label=""
              className="share-textarea"
              rows={6}
              isErrored={get(errors, ['notes', 'message'], false)}
              errorText={get(errors, ['notes', 'message'], '')}
            />
          </div>
          <div className="share-buttons">
            <div className="spacer" />
            <Button
              size="normal"
              inline
              type="secondary"
              onClick={() => {
                setAnchorEl(null);
                reset({ emails: [], notes: '' });
              }}
            >
              {t('cancel')}
            </Button>
            <Button
              size="normal"
              inline
              type="primary"
              disabled={isLoading}
              onClick={() => {
                handleSubmit(onSubmit, (err) => console.log('ERR', err))();
              }}
            >
              {t('share')}
            </Button>
          </div>
        </StyledSharePopover>
      </Popover>
    </>
  );
};
